// Third-party
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import ZendeskUtils from '@/utils/3rd-party/zendesk';

// Components
import ComparisonDetailsSection from './ComparisonDetailsSection';
import FullPageModal from '@/views/components/modals/FullPageModal';
import Modal from '@/views/components/modals/AccessibleModal';
import MoreOptionsSection from './MoreOptionsSection';
import ProposalEditorOptionsHeader from '@/views/components/modals/proposal-editor-options/ProposalEditorOptionsHeader';

// Types
import { ReduxState } from '@/redux/modules/types';
import Comparison from '@/types/comparison';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import { formatMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

type HandleProps = {
  setComparisonName: (name: string) => void;
};

type StateProps = {
  user: Nullable<User>;
};

type Props = {
  comparison: Comparison;
  isSaving: boolean;
  show: boolean;
  goBackToComparison: () => void;
  onChangeTemplate: () => void;
  onCloseModal: () => void;
  onSaveComparison: () => void;
  redirectToContactPage: () => void;
  refreshTemplate: () => void;
} & HandleProps &
  StateProps;

const ProposalEditorMobileMenuModal = (props: Props) => {
  const { comparison, isSaving, show, user, goBackToComparison, onChangeTemplate, onCloseModal, onSaveComparison, redirectToContactPage, refreshTemplate } =
    props;
  const DEFAULT_COMPARISON_NAME = formatMessage('Comparison.DefaultName');

  const handleChangeTemplate = () => {
    onCloseModal();
    onChangeTemplate();
  };

  const handleSaveComparison = () => {
    onCloseModal();
    onSaveComparison();
  };

  const handleRefreshTemplate = () => {
    onCloseModal();
    refreshTemplate();
  };

  useEffect(() => {
    if (show) {
      ZendeskUtils.hideWidget();
    }
    return () => {
      if (userHasPermission(user, PERMISSIONS.CHAT_SUPPORT)) {
        ZendeskUtils.showWidget();
      }
    };
  }, [show, user]);

  const onComparisonNameChange = debounce((name: string) => {
    let trimmedName = name.trim();

    if (!trimmedName) {
      trimmedName = DEFAULT_COMPARISON_NAME;
    }

    props.setComparisonName(trimmedName);
  }, 250);

  return (
    <FullPageModal show={show} className="proposal-editor-mobile-menu-modal" onExited={onCloseModal}>
      <ProposalEditorOptionsHeader title="Proposal.Tabs.Options.Menu" onBack={goBackToComparison} onExit={onCloseModal} />
      <Modal.Body className="proposal-editor-options-body">
        <ComparisonDetailsSection
          comparison={comparison}
          isSaving={isSaving}
          onComparisonNameChange={onComparisonNameChange}
          redirectToContactPage={redirectToContactPage}
        />
        <MoreOptionsSection onChangeTemplate={handleChangeTemplate} onSaveComparison={handleSaveComparison} refreshTemplate={handleRefreshTemplate} />
      </Modal.Body>
    </FullPageModal>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user || null,
});

export default connect(mapStateToProps)(ProposalEditorMobileMenuModal);
